import { useState, useEffect } from 'react';
import { FileJson, Loader2, RefreshCw, Calendar, Eye, FileText, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { getApiUrl } from '@/lib/api';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

interface EmailLog {
  filename: string;
  path: string;
  createdAt: string;
  range: {
    startDate: string;
    endDate: string;
  };
}

interface HtmlFile {
  filename: string;
  regional: string;
}

interface EndedProcessesReport {
  generatedAt: string;
  range: { startDate: string; endDate: string };
  totals: { processes: number; withPdfUrl: number };
  regionals: Record<string, {
    regionalName: string;
    totalDocuments: number;
    units: Record<string, {
      unitGuid: string;
      unitName: string;
      documents: Array<{
        processGuid: string;
        documentTypeName: string;
        unitName: string;
        cnpj: string | null;
        pdfUrl: string | null;
      }>;
    }>;
  }>;
}

export function AdminEmailsPage() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [selectedReport, setSelectedReport] = useState<EndedProcessesReport | null>(null);
  const [selectedHtmlFiles, setSelectedHtmlFiles] = useState<HtmlFile[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewRegional, setPreviewRegional] = useState<string>('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { toast } = useToast();

  // Carregar logs ao montar
  useEffect(() => {
    loadLogs();
  }, []);

  // Gerar relatório de processos concluídos
  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      toast({
        title: 'Datas obrigatórias',
        description: 'Informe a data inicial e final.',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(
        getApiUrl(`/claro/api/email/ended-processes?startDate=${startDate}&endDate=${endDate}`)
      );
      const data = await response.json();

      if (data.status === 'success') {
        toast({
          title: 'Relatório gerado',
          description: `${data.result.totals.processes} processos encontrados. ${data.htmlPaths?.length || 0} HTMLs gerados.`,
        });
        // Recarregar logs
        await loadLogs();
      } else {
        throw new Error(data.message || 'Erro ao gerar relatório');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao gerar relatório',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Carregar lista de logs
  const loadLogs = async () => {
    setIsLoadingLogs(true);
    try {
      const response = await fetch(getApiUrl('/claro/api/email/logs'));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.status === 'success') {
        setLogs(data.logs || []);
      } else {
        throw new Error(data.message || 'Erro ao carregar logs');
      }
    } catch (error) {
      toast({
        title: 'Erro ao carregar logs',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Carregar conteúdo de um log específico
  const handleViewLog = async (log: EmailLog) => {
    try {
      const response = await fetch(getApiUrl(`/claro/api/email/logs/${encodeURIComponent(log.filename)}`));
      const data = await response.json();
      if (data.status === 'success') {
        setSelectedReport(data.report);
        setSelectedHtmlFiles(data.htmlFiles || []);
        setIsDialogOpen(true);
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar o relatório.',
        variant: 'destructive',
      });
    }
  };

  // Visualizar HTML de uma regional
  const handleViewHtml = async (htmlFile: HtmlFile) => {
    try {
      const response = await fetch(getApiUrl(`/claro/api/email/preview/${encodeURIComponent(htmlFile.filename)}`));
      if (response.ok) {
        const html = await response.text();
        setPreviewHtml(html);
        setPreviewRegional(htmlFile.regional);
        setIsPreviewOpen(true);
      } else {
        throw new Error('Erro ao carregar preview');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar o preview.',
        variant: 'destructive',
      });
    }
  };

  // Abrir HTML em nova aba
  const handleOpenInNewTab = (htmlFile: HtmlFile) => {
    window.open(getApiUrl(`/claro/api/email/preview/${encodeURIComponent(htmlFile.filename)}`), '_blank');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Emails</h2>
        <p className="text-muted-foreground">
          Geração e histórico de emails de processos concluídos
        </p>
      </div>

      {/* Gerador de relatório */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Gerar Relatório de Processos Concluídos
          </CardTitle>
          <CardDescription>
            Busca processos concluídos no período e gera relatório para envio de email
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Data Inicial</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">Data Final</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={handleGenerate} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Gerar Relatório
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Histórico de logs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileJson className="h-5 w-5" />
                Logs de Emails Diários
              </CardTitle>
              <CardDescription>
                Histórico de relatórios gerados (automático via cron ou manual)
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadLogs} disabled={isLoadingLogs}>
              {isLoadingLogs ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingLogs ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum log encontrado
            </p>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.filename}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileJson className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">{log.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        Período: {log.range.startDate} a {log.range.endDate}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleViewLog(log)}>
                    <Eye className="h-4 w-4 mr-2" />
                    Visualizar
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para visualizar relatório com HTMLs */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Relatório de Processos Concluídos</DialogTitle>
            <DialogDescription>
              {selectedReport && (
                <>
                  Período: {selectedReport.range.startDate} a {selectedReport.range.endDate} | 
                  Total: {selectedReport.totals.processes} processos
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {/* HTMLs por Regional */}
          {selectedHtmlFiles.length > 0 && (
            <div className="border-b pb-4 mb-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Previews por Regional
              </h4>
              <div className="flex flex-wrap gap-2">
                {selectedHtmlFiles.map((htmlFile) => (
                  <div key={htmlFile.filename} className="flex items-center gap-1">
                    <Badge 
                      variant="secondary" 
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                      onClick={() => handleViewHtml(htmlFile)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      {htmlFile.regional}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleOpenInNewTab(htmlFile)}
                      title="Abrir em nova aba"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ScrollArea className="h-[50vh]">
            {selectedReport && (
              <div className="space-y-4 pr-4">
                {Object.entries(selectedReport.regionals).map(([regionalKey, regional]) => (
                  <Card key={regionalKey}>
                    <CardHeader className="py-3">
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span>{regional.regionalName} ({regional.totalDocuments} documentos)</span>
                        {selectedHtmlFiles.find(h => h.regional === regionalKey) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewHtml(selectedHtmlFiles.find(h => h.regional === regionalKey)!)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Ver Email
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {Object.entries(regional.units).map(([unitKey, unit]) => (
                          <div key={unitKey} className="text-sm">
                            <p className="font-medium">{unit.unitName}</p>
                            <ul className="ml-4 text-muted-foreground">
                              {unit.documents.map((doc, idx) => (
                                <li key={idx}>
                                  • {doc.documentTypeName}
                                  {doc.cnpj && <span className="ml-2 text-xs">({doc.cnpj})</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog para preview do HTML */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Preview Email - {previewRegional}</DialogTitle>
            <DialogDescription>
              Visualização do email que será enviado para esta regional
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 pt-2">
            <div className="border rounded-lg overflow-hidden bg-white">
              {previewHtml && (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-[70vh] border-0"
                  title={`Preview ${previewRegional}`}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
